import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LuArrowRight, LuFileSpreadsheet, LuPencil, LuShieldOff, LuChartBar } from 'react-icons/lu';
import HeaderOne from '../../../header/HeaderOne';
import FooterOne from '../../../footer/FooterOne';
import Head from '../../../components/Head';
import '../../landing.css';

const FEATURES = [
  { icon: <LuFileSpreadsheet size={16} strokeWidth={2} />, title: 'CSV upload, no limit', body: 'Drag in a contact list with thousands of rows. Map columns to placeholders in your message.' },
  { icon: <LuPencil size={16} strokeWidth={2} />, title: 'Personalized per recipient', body: 'Use {{first_name}}, {{order_id}}, anything from your CSV. Each recipient gets a unique message.' },
  { icon: <LuShieldOff size={16} strokeWidth={2} />, title: 'Auto opt-out handling', body: 'STOP / UNSUBSCRIBE replies are blocked across your account automatically — no manual scrubbing.' },
  { icon: <LuChartBar size={16} strokeWidth={2} />, title: 'Live delivery dashboard', body: 'Watch sends in real-time: queued, delivered, failed, replies. Re-run failed numbers in one click.' },
];

const STEPS = [
  { n: 1, title: 'Upload your contacts', body: 'Drag in a CSV. We auto-detect phone columns and dedupe rows.' },
  { n: 2, title: 'Write your message template', body: 'Plain text plus {{column_name}} placeholders. Preview a sample before you send.' },
  { n: 3, title: 'Hit send', body: 'We pace delivery across your SIMs to keep carriers happy. You get a live dashboard while it runs.' },
];

export default function AdvancedBulkSms() {
  const navigate = useNavigate();

  return (
    <>
      <Head
        title="Bulk SMS Software — Send Mass Texts via Your SIM"
        description="No-code bulk SMS sender. Upload a CSV, write a personalized message template, send to thousands. Carrier-friendly pacing, built-in opt-out handling, live delivery dashboard. $0.001 per SMS."
        canonical="/bulksms"
      />
      <HeaderOne />

      <main className="lp">
        <div className="lp-shell">

          <header className="lp-hero">
            <span className="lp-eyebrow">Advanced Bulk SMS</span>
            <h1 className="lp-h1">
              Personalized mass SMS.<br />
              <span className="lp-h1-accent">Zero code required.</span>
            </h1>
            <p className="lp-lead">
              Upload a CSV, write a message template with merge fields, hit send. We deliver personalized SMS to thousands of contacts — paced so carriers don't flag your numbers.
            </p>
            <div className="lp-cta-row">
              <button className="lp-btn lp-btn--primary" onClick={() => navigate('/signup')}>
                Try free <LuArrowRight size={15} strokeWidth={2} />
              </button>
              <Link className="lp-btn lp-btn--ghost" to="/pricing">See pricing</Link>
            </div>
            <div className="lp-meta">
              <span>300 free messages</span>
              <span className="lp-meta-sep" />
              <span>No credit card</span>
              <span className="lp-meta-sep" />
              <span>$0.001 per SMS</span>
            </div>
          </header>

          <section className="lp-section">
            <h2 className="lp-h2">How it works</h2>
            <p className="lp-sub">Three steps from contact list to delivered campaign.</p>

            <ol className="lp-steps">
              {STEPS.map((s) => (
                <li key={s.n} className="lp-step">
                  <div className="lp-step-num">{s.n}</div>
                  <div className="lp-step-body">
                    <h3 className="lp-step-title">{s.title}</h3>
                    <p className="lp-step-text">{s.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <section className="lp-section">
            <h2 className="lp-h2">Built for real campaigns</h2>
            <p className="lp-sub">Everything you'd build yourself if you had three months.</p>
            <ul className="lp-feat-list">
              {FEATURES.map((f, i) => (
                <li key={i} className="lp-feat">
                  <div className="lp-feat-icon">{f.icon}</div>
                  <div className="lp-feat-text">
                    <strong>{f.title}</strong>
                    <span>{f.body}</span>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section className="lp-section">
            <h2 className="lp-h2">Use cases</h2>
            <p className="lp-sub">What our customers run through bulk SMS every week.</p>
            <div className="lp-grid-3">
              <div className="lp-card">
                <h3 className="lp-card-h">Marketing blasts</h3>
                <p className="lp-card-p">Promo codes, flash sales, restock alerts. 95%+ open rate within 3 minutes.</p>
              </div>
              <div className="lp-card">
                <h3 className="lp-card-h">Appointment reminders</h3>
                <p className="lp-card-p">Clinics, salons, dispatch. Cut no-shows by 30–50% with 24h advance reminders.</p>
              </div>
              <div className="lp-card">
                <h3 className="lp-card-h">Reactivation campaigns</h3>
                <p className="lp-card-p">Win back lapsed customers with personalized offers based on past purchase columns.</p>
              </div>
            </div>
            <div className="lp-callout">
              <strong>Programmatic sender?</strong> The <Link to="/bulksmsapi" style={{ color: 'inherit', textDecoration: 'underline' }}>Bulk SMS API</Link> is the same engine, REST-callable.
            </div>
          </section>

          <section className="lp-cta">
            <h2 className="lp-h2">Send your first campaign today.</h2>
            <p className="lp-sub">No credit card. 300 free messages on signup.</p>
            <div className="lp-cta-row">
              <button className="lp-btn lp-btn--primary" onClick={() => navigate('/signup')}>
                Try free <LuArrowRight size={15} strokeWidth={2} />
              </button>
              <Link className="lp-btn lp-btn--ghost" to="/faq">FAQ</Link>
            </div>
          </section>

        </div>
      </main>

      <FooterOne />
    </>
  );
}
